import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

export type PlanName = 'Free' | 'Pro' | 'Enterprise';

export interface SubscriptionInfo {
  plan: PlanName;
  status: string | null;
  currentPeriodEnd: string | null;
  loading: boolean;
  refresh: () => Promise<void>;
}

export function useSubscription(): SubscriptionInfo {
  const { user } = useAuth();
  const [plan, setPlan] = useState<PlanName>('Free');
  const [status, setStatus] = useState<string | null>(null);
  const [currentPeriodEnd, setCurrentPeriodEnd] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    if (!user) {
      setPlan('Free');
      setStatus(null);
      setCurrentPeriodEnd(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    const { data, error } = await supabase
      .from('subscribers')
      .select('plan, status, current_period_end')
      .eq('email', user.email!)
      .maybeSingle();

    if (error || !data) {
      setPlan('Free');
      setStatus(null);
      setCurrentPeriodEnd(null);
    } else {
      const isActive = data.status === 'active' || data.status === 'trialing';
      setPlan(isActive && (data.plan === 'Pro' || data.plan === 'Enterprise') ? (data.plan as PlanName) : 'Free');
      setStatus(data.status);
      setCurrentPeriodEnd(data.current_period_end);
    }
    setLoading(false);
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [user?.id]);

  return { plan, status, currentPeriodEnd, loading, refresh: load };
}
